import Axios from '../api/axios';
import * as glb from '../globals';
import {
  idbReplaceIndex,
  idbSetMeta,
  idbGetMeta,
  idbPutBundle,
  idbGetBundle,
  idbClearEntityCaches,
  idbRemoveIndex,
  idbUpsertIndexRow,
  idbTrimBundles,
  migrateLegacyCariDbIfNeeded,
} from '../utils/syncIndexedDb';
import {
  setCached,
  clearCached,
  cariRecCacheKey,
  carRefCacheKey,
  casRefCacheKey,
  getCached,
} from '../utils/requestCache';
import { getRecentEntities, removeRecentEntity } from '../utils/recentEntities';
import {
  registerNotificationHandler,
  subscribeNotifications,
  handleIncomingNotification,
} from './notificationService';
import { broadcastBundleUpdated, initSyncBroadcast, subscribeSyncBroadcast } from '../utils/syncBroadcast';
import { emitCariSync } from '../utils/cariSyncBus';
import {
  CARI_BUNDLE_SCHEMA_VERSION,
  buildCariBundleCacheEntries,
  isCariBundleAuthError,
  selectFreshCariBundle,
  validateCariBundle,
} from '../utils/cariBundleReadModel';

const ENTITY = 'cari';
const BATCH_SIZE = 15;
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const MAX_BUNDLES = 250;
let warmupRunning = false;
let bootstrapDone = false;
let pollTimer = null;
let bundleAuthWarningShown = false;
const memoryBundles = new Map();

function cid() {
  return glb.default?.KULLANICI?.KUL_DEF_FIR_REF;
}

function applyCariBundleToMemoryCache(bundle) {
  const validation = validateCariBundle(bundle);
  if (!validation.valid) return false;
  const current = memoryBundles.get(validation.carRef);
  const selected = selectFreshCariBundle(bundle, current, validation.carRef);
  if (selected !== bundle) return false;
  memoryBundles.set(validation.carRef, bundle);
  buildCariBundleCacheEntries(bundle, cid()).forEach(([key, value]) => setCached(key, value));
  return true;
}

function indexRowFromBundle(bundle, entityId) {
  const kart = bundle?.carikart;
  if (!kart) return null;
  return {
    CAR_REF: entityId,
    CAR_ADSOY: kart.CAR_ADSOY,
    CAR_GSM: kart.CAR_GSM,
    CAR_TEL1: kart.CAR_TEL1,
    CAR_ARA_KUL_REF: kart.CAR_ARA_KUL_REF,
    CAR_GIR_TAR: kart.CAR_GIR_TAR,
    CAR_EKIM_TAR: kart.CAR_EKIM_TAR,
    CAR_UP_TAR: kart.CAR_UP_TAR || kart.CAR_GIR_TAR,
  };
}

async function notifyCariPanels(entityId, entity = ENTITY) {
  emitCariSync({ carRef: entityId, source: 'sync' });
  const detail = { carRef: entityId, entity };
  window.dispatchEvent(new CustomEvent('cari-sync-updated', { detail }));
  broadcastBundleUpdated(detail);
}

export async function applyEntityBundle(entity, bundle, { persist = true } = {}) {
  if (!bundle) return false;
  if (entity === 'cari' && !applyCariBundleToMemoryCache(bundle)) return false;
  if (persist) {
    await idbPutBundle(entity, bundle);
    await idbTrimBundles(entity, MAX_BUNDLES);
  }
  return true;
}

async function fetchBundlesBatch(entity, ids) {
  if (!ids.length) return [];
  try {
    const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}sync/bundle`, {
      params: { entity, ids: ids.join(',') },
    });
    bundleAuthWarningShown = false;
    return Array.isArray(res.data?.bundles) ? res.data.bundles : [];
  } catch (error) {
    if (!isCariBundleAuthError(error)) throw error;
    if (!bundleAuthWarningShown) {
      bundleAuthWarningShown = true;
      console.warn('[sync:cari] bundle yetkilendirmesi başarısız; mevcut endpoint fallback kullanılacak.');
    }
    return [];
  }
}

function buildWarmupQueue(warmup, recentIds) {
  const tiers = warmup || {};
  const seen = new Set();
  const queue = [];

  const addTier = (refs) => {
    for (const id of refs || []) {
      const n = Number(id);
      if (!n || seen.has(n)) continue;
      seen.add(n);
      queue.push(n);
    }
  };

  addTier(tiers.tasks);
  addTier(tiers.todayAppointments);
  addTier(tiers.tomorrowOperations);
  addTier(recentIds);
  addTier(tiers.rest);

  return queue;
}

async function runBackgroundWarmup(entity, warmup, { maxCount = 40 } = {}) {
  if (warmupRunning || entity !== 'cari') return;
  warmupRunning = true;
  try {
    const recentIds = getRecentEntities(entity, 15);
    const queue = buildWarmupQueue(warmup, recentIds).slice(0, maxCount);

    for (let i = 0; i < queue.length; i += BATCH_SIZE) {
      const batch = queue.slice(i, i + BATCH_SIZE);
      const bundles = await fetchBundlesBatch(entity, batch);
      for (const bundle of bundles) {
        await applyEntityBundle(entity, bundle);
      }
      await new Promise((r) => setTimeout(r, 80));
    }
  } finally {
    warmupRunning = false;
  }
}

export async function pollSyncChanges(entity = ENTITY) {
  const meta = await idbGetMeta(`sync_state:${entity}`);
  if (!meta?.cursor) return;

  try {
    const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}sync/changes`, {
      params: { entity, since: meta.cursor },
    });
    const data = res.data || {};
    const updatedIds = Array.isArray(data.updatedIds) ? data.updatedIds : [];

    for (const id of updatedIds) {
      await refreshEntityBundle(entity, id);
      await notifyCariPanels(id, entity);
    }

    if (data.cursor) {
      await idbSetMeta(`sync_state:${entity}`, { ...meta, cursor: data.cursor, lastPollAt: Date.now() });
    }
  } catch (err) {
    console.warn(`[sync:${entity}] poll failed`, err?.message || err);
  }
}

export function startSyncPolling(intervalMs = POLL_INTERVAL_MS) {
  stopSyncPolling();
  // Cron ayrı süreçte WS basamaz. Soket açıkken de poll, yeni cariyi listeye alır.
  pollTimer = setInterval(() => {
    void pollSyncChanges(ENTITY);
  }, intervalMs);
}

export function stopSyncPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

export async function runSyncBootstrap(entity = ENTITY, { warmup = true } = {}) {
  const kulid = glb.default?.KULLANICI?.KUL_REF;
  if (!kulid) return null;

  await migrateLegacyCariDbIfNeeded();

  try {
    const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}sync/bootstrap`, {
      params: { entity, months: 2 },
    });
    const data = res.data || {};
    const index = Array.isArray(data.index) ? data.index : [];

    await idbSetMeta(`sync_state:${entity}`, {
      entity,
      kulRef: kulid,
      admin: !!data.admin,
      cid: data.cid,
      cursor: data.cursor,
      lastBootstrapAt: Date.now(),
      totalCount: index.length,
      warmup: data.warmup || null,
      since: data.since || null,
      maxGirTar: data.maxGirTar || null,
      windowMonths: data.windowMonths ?? 2,
    });
    await idbReplaceIndex(entity, index);
    // Index bootstrap ile yenilenir. Eski arama/açılış bundle'ları silinmez (LRU).
    // CAR_GIR_TAR purge yapılmaz — arama/ekim aktivitesiyle gelen eski cariler index'te kalır.
    await idbTrimBundles(entity, MAX_BUNDLES);
    bootstrapDone = true;

    if (warmup) {
      void runBackgroundWarmup(entity, data.warmup);
    }
    return data;
  } catch (err) {
    console.warn(`[sync:${entity}] bootstrap failed`, err?.message || err);
    return null;
  }
}

export async function refreshEntityBundle(entity, entityId) {
  const id = Number(entityId);
  if (!id) return null;
  const bundles = await fetchBundlesBatch(entity, [id]);
  const bundle = bundles[0];
  if (bundle) {
    const applied = await applyEntityBundle(entity, bundle);
    if (!applied) return memoryBundles.get(id) || null;
    const row = indexRowFromBundle(bundle, id);
    if (row) await idbUpsertIndexRow(entity, row);
  }
  return bundle || null;
}

export async function removeLocalEntity(entity, entityId) {
  const id = Number(entityId);
  if (!id) return;
  await idbRemoveIndex(entity, id);
  await idbClearEntityCaches(entity, id);
  removeRecentEntity(entity, id);
  if (entity === 'cari') {
    const satis = getCached(carRefCacheKey('SATIS_LST', id));
    clearCached(cariRecCacheKey(id, cid(), 'update'));
    clearCached(carRefCacheKey('SATIS_LST', id));
    clearCached(carRefCacheKey('ISLEMLIST_BY_CAR', id));
    clearCached(carRefCacheKey('PARA_LIST_BY_CAR', id));
    clearCached(carRefCacheKey('RESIM_META', id));
    memoryBundles.delete(id);
    if (Array.isArray(satis)) {
      for (const row of satis) {
        const cas = Number(row?.CAS_REF ?? 0);
        if (cas > 0) clearCached(casRefCacheKey('EKIMKANALALIM_LIST', cas));
      }
    }
  }
}

export async function hydrateBundleFromIdb(entity, entityId) {
  const bundle = await idbGetBundle(entity, entityId);
  if (bundle && entity === 'cari') {
    return applyCariBundleToMemoryCache(bundle) ? bundle : null;
  }
  return bundle;
}

/** Sadece bellek — senkron, network/IDB yok. */
export function peekLocalCariBundle(carRef) {
  const id = Number(carRef);
  if (!id) return null;
  return memoryBundles.get(id) || null;
}

/**
 * Sadece yerel: bellek → IndexedDB. Network yok.
 * Kart açılışında UI'yi bloklamadan hızlı bind için.
 */
export async function loadLocalCariBundle(carRef) {
  const id = Number(carRef);
  if (!id) return null;
  const peeked = peekLocalCariBundle(id);
  if (peeked) return peeked;
  const idbBundle = await hydrateBundleFromIdb('cari', id);
  if (idbBundle?.carikart) {
    return idbBundle;
  }
  return null;
}

/** Liste prefetch: IDB→bellek (network yok). */
export function warmCariBundleFromIdb(carRef) {
  const id = Number(carRef);
  if (!id) return;
  if (peekLocalCariBundle(id)) return;
  void hydrateBundleFromIdb('cari', id);
}

/**
 * Arama/açılış: yerelde yoksa sunucudan tam bundle çek (tarih penceresi dışı cari dahil).
 * Bağlı satış/işlem/para için sunucu tarafında tarih filtresi yok.
 */
export async function ensureEntityBundleCached(entity, entityId) {
  const id = Number(entityId);
  if (!id || entity !== 'cari') return null;
  let local = null;
  try {
    local = await loadLocalCariBundle(id);
  } catch (_) {
    // IndexedDB kapalı/bozuksa mevcut network bundle fallback'i çalışsın.
  }
  if (local && Number(local.schemaVersion) === CARI_BUNDLE_SCHEMA_VERSION) return local;
  try {
    return await refreshEntityBundle(entity, id);
  } catch (error) {
    console.warn(`[sync:${entity}] bundle yüklenemedi; endpoint fallback kullanılacak.`, error?.message || error);
    return local;
  }
}

/** Birden fazla cari için: önce yerel, eksikler batch bundle. */
export function ensureCariBundlesCached(carRefs) {
  const ids = [...new Set((carRefs || []).map((x) => Number(x)).filter((n) => n > 0))];
  if (!ids.length) return;
  void (async () => {
    const missing = [];
    for (const id of ids) {
      if (Number(peekLocalCariBundle(id)?.schemaVersion) === CARI_BUNDLE_SCHEMA_VERSION) continue;
      const local = await loadLocalCariBundle(id);
      if (!local || Number(local.schemaVersion) !== CARI_BUNDLE_SCHEMA_VERSION) missing.push(id);
    }
    for (let i = 0; i < missing.length; i += BATCH_SIZE) {
      const batch = missing.slice(i, i + BATCH_SIZE);
      const bundles = await fetchBundlesBatch(ENTITY, batch);
      for (const bundle of bundles) {
        await applyEntityBundle(ENTITY, bundle);
        const row = indexRowFromBundle(bundle, Number(bundle.entityId ?? bundle.carRef));
        if (row) await idbUpsertIndexRow(ENTITY, row);
      }
    }
  })();
}

export function initSyncNotificationHandlers() {
  const unsubBroadcast = subscribeSyncBroadcast((n) => {
    handleIncomingNotification(n);
  });

  const unsubGlobal = subscribeNotifications((n) => {
    const data = n?.data || {};
    const entity = data.entity || ENTITY;
    const entityId = Number(data.entityId ?? data.carRef ?? 0);

    if (n.type === 'SYNC_INVALIDATE' && entityId) {
      void refreshEntityBundle(entity, entityId).then((bundle) => {
        if (bundle) void notifyCariPanels(entityId, entity);
      });
    }
    if (n.type === 'CARI_UNASSIGNED' && entityId) {
      void removeLocalEntity('cari', entityId);
      window.dispatchEvent(new CustomEvent('cari-sync-local-removed', { detail: { carRef: entityId } }));
    }
  });

  const unregBadge = registerNotificationHandler('CARI_ASSIGNMENT_BADGE', () => { /* hook uses global event */ });

  return () => {
    unsubBroadcast();
    unsubGlobal();
    unregBadge();
  };
}

export function isSyncBootstrapDone() {
  return bootstrapDone;
}

/** Geriye dönük alias */
export const runCariSyncBootstrap = (opts) => runSyncBootstrap(ENTITY, opts);
export const refreshCariBundle = (carRef) => refreshEntityBundle(ENTITY, carRef);
export const removeLocalCari = (carRef) => removeLocalEntity(ENTITY, carRef);
export const ensureCariBundleCached = (carRef) => ensureEntityBundleCached(ENTITY, carRef);
export const initCariSyncListeners = initSyncNotificationHandlers;
export { initSyncBroadcast };
